<i18n>
{
  "en": {
    "agents": {
      "id": "ID",
      "url": "URL",
      "token": "Token",
      "processesPeriodicity": "Periodicity processes, m",
      "status": "Status",
      "title": "Agents",
      "location": "Location",
      "version": "Agent version",
      "configVersion": "Config version",
      "lastActivity": "Last activity",
      "grouped": "Group by locations",
      "create": {
        "hint": "Add new agent manually"
      }
    }
  },
  "ru": {
    "agents": {
      "id": "ID",
      "url": "URL",
      "token": "Токен",
      "processesPeriodicity": "Периодичность опроса, м",
      "status": "Статус",
      "title": "Агенты",
      "location": "Локация",
      "version": "Версия агента",
      "configVersion": "Версия конфига",
      "lastActivity": "Последняя активность",
      "grouped": "Группировать по локациям",
      "create": {
        "hint": "Зарегистрировать нового агента вручную"
      }
    }
  }
}
</i18n>

<template>
  <div class="content-block" id="agents">
    <div class="grid-toolbar">
      <div class="toolbar-left">
        <dx-button type="normal"
          :text="$t('general.resetFilter')"
          styling-mode="contained"
          @click="onResetState"
        />
        
        <dx-button
          id="create"
          icon="plus"
          type="normal"
          :text="$t('general.create')"
          :hint="$t('agents.create.hint')"
          @click="onCreate"
        />
      </div>

      <div class="toolbar-right">
        <span>{{ $t('agents.grouped') }}</span>
        <dx-switch
          v-model="isGrouped"
          :width="50"
        />
      </div>
    </div>

    <dx-data-grid
      class="data-grid dx-card"
      :data-source="dataSource"
      :syncLookupFilterValues="false"
      :height="gridHeight"
      @row-click="onRowClick"
      @content-ready="onContentReady"
      @context-menu-preparing="addMenuItems"
      @option-changed="onOptionChanged"
      @editor-preparing="onEditorPreparing"
      @initialized=initializedGrid
      ref="dataGrid"
    >
      <dx-paging :enabled="true" />
      <dx-pager
        :visible="true"
        :allowed-page-sizes="pageSize"
        display-mode="full"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="false"
      />
      <dx-filter-row :visible="true" />
      <dx-filter-panel :visible="showFilterPanel" />
      <dx-sorting mode="multiple" />
      <dx-group-panel :visible="false" :allowColumnDragging="false" />
      <dx-grouping :auto-expand-all="true" />
      <dx-state-storing
        :enabled="true"
        type="custom"
        :customLoad="stateLoad"
        :customSave="stateSave"
      />

      <dx-column
        :group-index="isGrouped ? 0 : null"
        sort-order="desc"
        data-field="location.name"
        :caption="$t('agents.location')"
        :width="160"
        :visible-index="0"
      />

      <dx-column
        data-field="url"
        :caption="$t('agents.url')"
        :visible-index="1"
      />

      <dx-column
        data-field="securityToken"
        :caption="$t('agents.token')"
        :width="300"
        :visible-index="2"
      />

      <dx-column
        data-field="processesPeriodicity"
        :caption="$t('agents.processesPeriodicity')"
        :width="190"
        :visible-index="3"
      />

      <dx-column
        data-field="status"
        :caption="$t('agents.status')"
        :width="165"
        :visible-index="4"
        cell-template="agent-status-cell"
        :calculate-sort-value="getStatusSortValue"
      >
        <dx-lookup
          :data-source="agentStatusDataSourceConfig"
          value-expr="id"
          display-expr="text"
        />
      </dx-column>

      <dx-column
        data-field="agentVersion"
        :caption="$t('agents.version')"
        :width="160"
        :visible-index="5"
      />

      <dx-column
        data-field="configVersion"
        :caption="$t('agents.configVersion')"
        :width="160"
        :visible-index="6"
      />

      <dx-column
        data-field="lastActivity"
        :caption="$t('agents.lastActivity')"
        :width="180"
        data-type="datetime"
        format="dd.MM.yyyy HH:mm:ss"
        :visible-index="7"
      />

      <template #agent-status-cell="{ data }">
        <div :style="{ color: formatAgentStatusColor(data.value) }">{{ data.displayValue }}</div>
      </template>
    </dx-data-grid>

    <CustomColumnChooser
      :visible="visible"
      :columns="columns"
      :onApply="onApply"
      :onHiding="onHiding"
    />
  </div>
</template>

<script>
import baseView from "../base";
import appInfo from "../../app-info";
import { formatAgentStatusColor, agentStatusDataSourceConfig, AgentStatus } from "./constants";

import DxDataGrid, {
  DxColumn, DxLookup, DxFilterRow, DxPaging, DxStateStoring, DxPager, DxSorting, DxGroupPanel, DxGrouping, DxFilterPanel
} from "devextreme-vue/data-grid";
import { DxSwitch } from 'devextreme-vue/switch';
import { DxButton } from 'devextreme-vue';

import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";

import { getAvailableAgents } from "@/services/api";
import { AclHandler } from "@/services/acl-handler";

import { DataDrid } from "@/mixins/dataDridMixin";
import { DataLocation } from "@/mixins/dataLocationMixin";

import {
  PAGE_HEADER_HEIGHT,
  PAGE_PADDING,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_FILTER_ROW_HEIGHT,
  GRID_FILTER_PANEL_HEIGHT,
  GRID_PAGER_HEIGHT
} from '@/shared/consts/layout';

export default {
  name: 'agents',
  extends: baseView,
  mixins: [DataDrid, DataLocation],
  components: {
    DxDataGrid, DxColumn, DxLookup, DxFilterRow, DxPaging, DxButton, DxStateStoring, CustomColumnChooser,
    DxPager, DxSorting, DxGroupPanel, DxGrouping, DxFilterPanel, DxSwitch
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      agentStatusDataSourceConfig,
      isGrouped: true,
      dataSource: [],
      saveFilter: true,
      optionChangedGridFlag: false,
    };
  },
  computed: {
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    showFilterPanel() {
      return this.aclUser.hasGrantForOperation('BTN_CLEAR_FILTER', 'EXECUTE') || false;
    },
    sumGap() {
      return PAGE_HEADER_HEIGHT + PAGE_PADDING + PAGE_TOOLBARS_HEIGHT + PAGE_FOOTER_HEIGHT;
    },
    gridHeight() {
      const appHeightSize = this.$store.getters.appContainerSize.height;
      return (appHeightSize || 800) - this.sumGap;
    },
    gridBodyHeight() {
      return this.gridHeight - GRID_HEADER_HEIGHT - GRID_FILTER_ROW_HEIGHT - (this.showFilterPanel ? GRID_FILTER_PANEL_HEIGHT : 0) - GRID_PAGER_HEIGHT;
    },
  },
  methods: {
    getStatusSortValue(rowData) {
      const status = AgentStatus[rowData.status];
      return status ? status.text : '';
    },
    formatAgentStatusColor,
    onEditorPreparing(e) {
      this.applyFilterRowTimeout(e);
    },
    onContentReady(e) {
      this.addFitToPageHeightButton(e);
    },
    stateSave(state) {
      if (this.saveFilter)
        return this.$store.commit('setGridsStates', { key: this.gridStateKey, state: state});
    },
    onOptionChanged(e) {
      if (e.fullName === "filterValue") {
        if (this.optionChangedGridFlag) {
          this.saveFilter = true;
        }
      }
    },
    onRowClick(e) {
      if (window.getSelection().focusOffset !== window.getSelection().anchorOffset || !e.data.id)
        return;

      if (e.event.metaKey || e.event.ctrlKey) {
        const routeData = this.$router.resolve({
          name: "agent",
          params: { id: e.data.id }
        });
        window.open(routeData.href, "_blank");
      } else {
        this.$router.push({
          name: "agent",
          params: { id: e.data.id }
        });
      }
    },
    onCreate() {
      this.$router.push({ name: "agent", params: { id: -1 } });
    },
    onResetState() {
      this.dataGrid.clearFilter();
    },
  },
  async created() {
    this.gridStateKey = 'gridAgents';

    document.title = `${appInfo.title} - ${this.$t('agents.title')}`;
    this.$store.commit("setBreadcrumb", [{
      link: "/agents",
      title: this.$t('agents.title')
    }]);

    try {
      this.dataSource = await getAvailableAgents();

      this.$nextTick(() => {
        const routeParams = this.$route.params;

        if (routeParams && routeParams.filter) {
          this.dataGrid.option('filterValue', routeParams.filter);
          this.optionChangedGridFlag = true;
          this.saveFilter = false;
        } else {
          this.saveFilter = true;
        }
      });
    } catch (error) {
      this.onDataError(error);
    }
  },
  mounted() {
    const state = this.$store.getters.gridsStates;
    if (state && state[this.gridStateKey]) {
      const columns = state[this.gridStateKey].columns || [];
      const column = columns.find(col => col.dataField === "location.name");
      this.isGrouped = column && column.groupIndex != null;
    } else {
      this.isGrouped = true;
    }
  },
  beforeDestroy() {
    const state = this.dataGrid.state();
    this.stateSave(state);
  },
  watch: {
    isGrouped(val) {
      if (val) {
        this.dataGrid.columnOption('location.name', 'groupIndex', 0);
      } else {
        this.dataGrid.columnOption('location.name', 'groupIndex', null);
      }
    },
  },
};
</script>

<style lang="scss">
#agents {
  .grid-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .grid-toolbar .toolbar-left {
    display: flex;
    gap: 8px;
  }

  .grid-toolbar .toolbar-right {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .dx-toolbar.dx-widget.dx-visibility-change-handler.dx-collection {
    padding: 10px 10px 0;
  }
}
</style>
